import { MrnSettings } from '@/features/patient-configuration/MrnSettings';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';
import { clientApi } from '@/config/axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { usePracticeConfiguration, flowsheetUrl } from './api';
import { PracticeConfigurationEditor } from './PracticeConfigurationEditor';
import { PracticeOnboarding } from './PracticeOnboarding';
import type { PracticeConfiguration } from './rules';
export function PracticeSetup() {
  const { permissions } = usePermissions();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Practice Setup</CardTitle>
        <CardDescription>
          Configure flowsheets, clinical rules, templates and active-client onboarding before charting.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <MrnSettings />
        <p>
          Assign explicit clinical permissions in Roles and Users. Administrative access alone does not allow reading,
          creating or signing clinical events.
        </p>
        {permissions.includes('configure_flowsheet') ? (
          <ConfigurationPanel />
        ) : (
          <p>
            Practice configuration requires the configure flowsheet permission. Ask your practice administrator to
            assign it to the appropriate clinical lead.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
function ConfigurationPanel() {
  const { organization } = useAuth();
  const config = usePracticeConfiguration();
  const cache = useQueryClient();
  return (
    <>
      {config.isError ? (
        <p role="alert">
          Practice settings could not be loaded. Confirm explicit flowsheet access and configuration permissions.
        </p>
      ) : config.data ? (
        <PracticeConfigurationEditor
          key={organization?.organization_id}
          configuration={config.data}
          publish={async (input) => {
            const { data } = await clientApi.post<PracticeConfiguration>(
              `${flowsheetUrl(organization!.organization_id)}/configuration`,
              input,
            );
            await cache.invalidateQueries({ queryKey: ['flowsheet-configuration', organization!.organization_id] });
            return data;
          }}
        />
      ) : (
        <p>Loading practice settings…</p>
      )}
      <PracticeOnboarding />
    </>
  );
}
